import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { CMSProvider } from './context/CMSContext';
import { CartProvider } from './context/CartContext';
import Header from './components/Header';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import AdminBar from './components/AdminBar';
import ScrollToTop from './components/ScrollToTop';
import RequireCustomer from './components/RequireCustomer';
import ShopPage from './pages/ShopPage';
import ContactPage from './pages/ContactPage';
import ProductPage from './pages/ProductPage';
import CalculatorPage from './pages/CalculatorPage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import AccountPage from './pages/AccountPage';
import VerifyPhonePage from './pages/VerifyPhonePage';
import CheckoutPage from './pages/CheckoutPage';
import PayPage from './pages/PayPage';
import ReorderPage from './pages/ReorderPage';
import DosingPage from './pages/DosingPage';
import LegalPage from './pages/LegalPage';
import LandingPage from './pages/LandingPage';
import { useAuth } from './context/AuthContext';
import { Loader2 } from 'lucide-react';

function PublicLayout({ children }) {
  return (
    <>
      <Header />
      {children}
      <Footer />
      <CartDrawer />
      <AdminBar />
    </>
  );
}

// Wraps a page behind the customer-login gate. /account itself is always public.
const Gated = ({ children }) => <RequireCustomer>{children}</RequireCustomer>;

// Smart root: logged-in customers see the shop, everyone else sees the landing page.
// This lets ad traffic land somewhere engaging instead of being bounced to /account.
const HomeRoute = () => {
  const { customer, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-red-500" size={28} /></div>;
  return customer ? <ShopPage /> : <LandingPage />;
};

function App() {
  return (
    <div className="App min-h-screen bg-white text-[#0a0a0a] font-sans antialiased">
      <BrowserRouter>
        <AuthProvider>
          <CMSProvider>
            <CartProvider>
              <ScrollToTop />
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/*" element={
                  <PublicLayout>
                    <Routes>
                      {/* /account and /verify-phone are the only pages a non-logged-in customer can see */}
                      <Route path="/account" element={<AccountPage />} />
                      <Route path="/verify-phone" element={<VerifyPhonePage />} />
                      {/* Everything else is gated — required for AU peptide compliance */}
                      <Route path="/" element={<HomeRoute />} />
                      <Route path="/shop" element={<Gated><ShopPage /></Gated>} />
                      <Route path="/contact" element={<Gated><ContactPage /></Gated>} />
                      <Route path="/reconstitution-calculator" element={<Gated><CalculatorPage /></Gated>} />
                      <Route path="/calculator" element={<Gated><CalculatorPage /></Gated>} />
                      <Route path="/dosing" element={<Gated><DosingPage /></Gated>} />
                      <Route path="/privacy" element={<LegalPage slug="privacy" />} />
                      <Route path="/terms" element={<LegalPage slug="terms" />} />
                      <Route path="/shipping" element={<LegalPage slug="shipping" />} />
                      <Route path="/refunds" element={<LegalPage slug="refunds" />} />
                      <Route path="/checkout" element={<Gated><CheckoutPage /></Gated>} />
                      <Route path="/pay/:reference" element={<Gated><PayPage /></Gated>} />
                      <Route path="/reorder/:reference" element={<Gated><ReorderPage /></Gated>} />
                      <Route path="/product/:slug" element={<Gated><ProductPage /></Gated>} />
                    </Routes>
                  </PublicLayout>
                } />
              </Routes>
              <Toaster position="top-right" richColors closeButton />
            </CartProvider>
          </CMSProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
